import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import DashboardLayout from '../../components/layout/DashboardLayout';
import ReleaseNoteItems, { ReleaseNoteItemVM } from './ReleaseNoteItems';
import { RELEASE_NOTE_QUERY } from '../../graphql/queries';
import styles from './ReleaseNoteDetail.module.css';

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });

const ReleaseNoteDetail = () => {
  const { version } = useParams<{ version: string }>();
  const navigate = useNavigate();
  const { data, loading, error } = useQuery(RELEASE_NOTE_QUERY, {
    variables: { version },
    skip: !version,
    fetchPolicy: 'cache-and-network',
  });
  const note = data?.releaseNote as { version: string; releaseDate: string; items: ReleaseNoteItemVM[] } | null | undefined;

  return (
    <DashboardLayout topBarTitle="Release Notes">
      <div className="space-y-6">
        <div>
          <button className={styles.backLink} onClick={() => navigate('/release-notes')}>
            <span className="material-symbols-outlined text-base">arrow_back</span>
            All release notes
          </button>
          {note && (
            <>
              <h2 className="text-3xl font-bold tracking-tight text-on-surface font-headline mt-2">
                Version {note.version}
              </h2>
              <p className="text-on-surface-variant mt-1">{formatDate(note.releaseDate)}</p>
            </>
          )}
        </div>

        {loading && !note ? (
          <div className="flex items-center justify-center py-16 text-on-surface-variant">
            <span className="material-symbols-outlined animate-spin mr-2">refresh</span>
            Loading...
          </div>
        ) : error && !note ? (
          <div role="alert" className="p-4 bg-error-container rounded-lg flex items-center gap-2 text-sm text-on-error-container">
            <span className="material-symbols-outlined text-base">error</span>
            Could not load release note {version}: {error.message}
          </div>
        ) : !note ? (
          <div className="py-16 text-center bg-white rounded-xl">
            <span className="material-symbols-outlined text-4xl text-outline mb-3 block">search_off</span>
            <p className="text-on-surface-variant">No release note found for version "{version}"</p>
          </div>
        ) : (
          <ReleaseNoteItems items={note.items} />
        )}
      </div>
    </DashboardLayout>
  );
};

export default ReleaseNoteDetail;
